import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 sm:p-12 bg-background">
      <div className="w-full max-w-sm" data-testid="forgot-password-page">
        <img src="/logo.png" alt="Caring Hands Concierge Nursing" className="h-20 w-20 object-contain mb-6" />
        <div className="label-caps mb-2">Reset password</div>
        <h2 className="font-display font-bold text-3xl tracking-tighter mb-8">Forgot your password?</h2>

        {sent ? (
          <div data-testid="forgot-password-sent">
            <div className="text-sm font-mono border border-primary/30 bg-primary/5 px-3 py-3 mb-6">
              If an account exists for <span className="font-bold">{email}</span>, we've sent a
              password reset link. Check your inbox — the link expires in 30 minutes.
            </div>
            <Button
              type="button"
              className="w-full rounded-none h-11"
              onClick={() => navigate("/login")}
              data-testid="back-to-login-button"
            >
              Back to sign in
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="forgot-password-form">
            <p className="text-sm text-muted-foreground mb-6">
              Enter your account email and we'll send you a secure link to set a new password.
            </p>
            <div className="mb-4">
              <Label className="label-caps">Email</Label>
              <Input
                type="email"
                className="rounded-none mt-2 h-11"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="forgot-email-input"
              />
            </div>

            {error && (
              <div className="mb-4 text-destructive text-sm font-mono border border-destructive/40 bg-destructive/5 px-3 py-2" data-testid="forgot-error">
                {error}
              </div>
            )}

            <Button type="submit" disabled={busy} className="w-full rounded-none h-11 mt-2" data-testid="forgot-submit-button">
              {busy ? "Sending…" : "Send reset link"}
            </Button>

            <button
              type="button"
              onClick={() => navigate("/login")}
              className="mt-6 w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
              data-testid="forgot-back-link"
            >
              ← Back to sign in
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
